import { extensionApiRef } from '@agilelab/plugin-wb-builder-catalog';
import { EntityErrorResult } from '@agilelab/plugin-wb-catalog-extension-common';
import {
  ALL_TAXONOMIES_FILTER,
  PracticeShaperFilters,
  useTaxonomySelection,
} from '@agilelab/plugin-wb-practice-shaper';
import { Entity } from '@mainstage/catalog-model';
import { identityApiRef, useApi } from '@mainstage/core-plugin-api';
import {
  EntityKindFilter,
  useEntityList,
} from '@mainstage/plugin-catalog-react';
import { TemplateEntityV1beta3 } from '@mainstage/plugin-scaffolder-common';
import { isEmpty } from 'lodash';
import {
  ComponentType,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

export type ViewType = 'table' | 'grid';

export interface ScaffolderPageContextType {
  viewType: ViewType;
  setViewType: Dispatch<SetStateAction<ViewType>>;

  entities: Entity[];
  loading: boolean;
  error: Error | undefined;
  filters: PracticeShaperFilters;
  updateFilters: (
    filters:
      | Partial<PracticeShaperFilters>
      | ((
          prevFilters: PracticeShaperFilters,
        ) => Partial<PracticeShaperFilters>),
  ) => void;

  TemplateCardComponent?:
    | ComponentType<{ template: TemplateEntityV1beta3 }>
    | undefined;

  errors: EntityErrorResult[] | undefined;
}

export const ScaffolderPageContext = createContext<ScaffolderPageContextType>(
  {} as ScaffolderPageContextType,
);

interface Props {
  children?: ReactNode;
  TemplateCardComponent?:
    | ComponentType<{ template: TemplateEntityV1beta3 }>
    | undefined;
}

export const ScaffolderPageContextProvider: FC<Props> = ({
  children,
  TemplateCardComponent,
}) => {
  const catalogApi = useApi(extensionApiRef);
  const identityApi = useApi(identityApiRef);
  const [viewType, setViewType] = useState<ViewType>('grid');

  const [errors, setEntityErrors] = useState<EntityErrorResult[]>([]);

  const { selectedTaxonomyRef } = useTaxonomySelection();

  const { entities, updateFilters, filters, loading, error } = useEntityList();

  const filteredEntities: Entity[] = useMemo(() => {
    if (selectedTaxonomyRef === ALL_TAXONOMIES_FILTER) {
      return entities;
    }

    return entities.filter(
      e =>
        isEmpty(e.metadata?.classDetails) || // entities with no taxonomy have to be always shown
        (e.metadata?.classDetails as { taxonomy?: string })?.taxonomy ===
          selectedTaxonomyRef,
    );
  }, [entities, selectedTaxonomyRef]);

  useEffect(() => {
    const templateFilters: Partial<PracticeShaperFilters> = {
      kind: new EntityKindFilter('template', 'template'),
    };
    updateFilters(templateFilters);
  }, [updateFilters, selectedTaxonomyRef]);

  useEffect(() => {
    if (filteredEntities.length > 0) {
      const fetchEntityErrors = async () => {
        try {
          const entityIds = filteredEntities.flatMap(e =>
            e.metadata.uid ? [e.metadata.uid] : [],
          );
          const { token } = await identityApi.getCredentials();
          const result = await catalogApi.getEntityErrors(entityIds, { token });
          setEntityErrors(result ?? []); // Update state with the fetched errors
        } catch (e) {
          setEntityErrors([]);
        }
      };
      fetchEntityErrors();
    }
  }, [filteredEntities, catalogApi, identityApi]);

  return (
    <ScaffolderPageContext.Provider
      value={{
        viewType,
        setViewType,

        entities: filteredEntities,
        loading,
        filters,
        updateFilters,
        error,

        TemplateCardComponent,
        errors,
      }}
    >
      {children}
    </ScaffolderPageContext.Provider>
  );
};
